import * as THREE from "three";
import * as RuntimeRegistry from "../../runtime/objectRegistry";
import { sanitizeMjcfName } from "../../utils/mjcfNames";
import type { Body, Site } from "../../types/scene";

export function resolveRuntimeObject(uuid: string): THREE.Object3D | null {
  const registry = RuntimeRegistry.getAll() || {};
  const obj =
    (registry[uuid] as THREE.Object3D | undefined) ||
    (registry[sanitizeMjcfName(uuid)] as THREE.Object3D | undefined);
  return obj || null;
}

export function resolveRuntimeBodyPos(uuid: string): THREE.Vector3 | null {
  const obj = resolveRuntimeObject(uuid);
  if (!obj) return null;
  const p = new THREE.Vector3();
  obj.getWorldPosition(p);
  return p;
}

/**
 * Match IK Target Picker semantics:
 * - Prefer runtime object's world-space bounding-box center
 * - Fallback to runtime body origin when bbox is unavailable
 */
export function resolveRuntimeBodyTargetPos(uuid: string): THREE.Vector3 | null {
  const obj = resolveRuntimeObject(uuid);
  if (!obj) return null;
  try {
    const box = new THREE.Box3().setFromObject(obj);
    if (!box.isEmpty()) {
      const center = new THREE.Vector3();
      box.getCenter(center);
      return center;
    }
  } catch {
    // ignore and fall back to body origin
  }
  const p = new THREE.Vector3();
  obj.getWorldPosition(p);
  return p;
}

/**
 * Resolve runtime world position of a named site by applying site local position
 * to the runtime transform of the body that owns the site.
 */
type RuntimeSiteScene = {
  sites?: Record<string, Site>;
  bodies?: Record<string, Body>;
};

export function resolveRuntimeSitePosByName(
  scene: RuntimeSiteScene | null | undefined,
  siteName: string
): THREE.Vector3 | null {
  if (!scene || !siteName) return null;
  const sites = scene?.sites || {};
  const bodies = scene?.bodies || {};
  const siteEntry = Object.values(sites).find((site) => site?.name === siteName);
  if (!siteEntry?.uuid || !Array.isArray(siteEntry?.position) || siteEntry.position.length < 3)
    return null;
  const parentBody = Object.values(bodies).find((body) =>
    Array.isArray(body?.sites) ? body.sites.includes(siteEntry.uuid) : false
  );
  if (!parentBody?.uuid) return null;
  const bodyObj = resolveRuntimeObject(String(parentBody.uuid));
  if (!bodyObj) return null;
  const v = new THREE.Vector3(
    Number(siteEntry.position[0]) || 0,
    Number(siteEntry.position[1]) || 0,
    Number(siteEntry.position[2]) || 0
  );
  bodyObj.localToWorld(v);
  return v;
}

export function distance(a: THREE.Vector3, b: THREE.Vector3): number {
  return a.distanceTo(b);
}
